/**
 * layout.js — Vuex module: Layout Settings
 *
 * Fungsi:
 * - menyimpan preferensi tampilan user: layout type (vertical/horizontal/twocolumn),
 *   mode (light/dark), warna sidebar, position, preloader, dll
 * - persist semua setting ke localStorage dengan prefix `layout.*`
 *   sehingga pilihan user di Right Bar (Customize Settings) tidak reset
 *   saat halaman di-refresh
 * - expose actions untuk ubah masing-masing setting
 *
 * API: TIDAK ADA. Semua state dikelola lokal (localStorage).
 *
 * Field yang di-manage:
 *   layoutType, layoutWidth, sidebarSize, topbar, mode, position,
 *   sidebarView, sidebarColor, sidebarImage, preloader, visibility
 */

/**
 * Helper untuk membaca setting dari localStorage kalau ada,
 * kalau tidak, pakai nilai default. Ini yang bikin pilihan user
 * di Right Bar (Customize Settings) tidak reset saat refresh.
 */
const getStored = (key, fallback) => {
  const value = localStorage.getItem(`layout.${key}`);
  return value !== null ? value : fallback;
};

export const state = {
  layoutType: getStored('layoutType', 'vertical'),
  layoutWidth: getStored('layoutWidth', 'fluid'),
  sidebarSize: getStored('sidebarSize', 'lg'),
  topbar: getStored('topbar', 'light'),
  mode: getStored('mode', 'light'),
  position: getStored('position', 'fixed'),
  sidebarView: getStored('sidebarView', 'default'),
  sidebarColor: getStored('sidebarColor', 'dark'),
  sidebarImage: getStored('sidebarImage', 'none'),
  preloader: getStored('preloader', 'disable'),
  visibility: getStored('visibility', 'show')
};

// Tiap mutation: update state + simpan ke localStorage.
// Dengan ini, setiap kali user ubah setting di Right Bar,
// nilainya langsung tersimpan dan dipakai lagi saat halaman dibuka berikutnya.
export const mutations = {
  CHANGE_LAYOUT(state, layoutType) {
    state.layoutType = layoutType;
    localStorage.setItem('layout.layoutType', layoutType);
  },
  CHANGE_LAYOUT_WIDTH(state, layoutWidth) {
    state.layoutWidth = layoutWidth;
    localStorage.setItem('layout.layoutWidth', layoutWidth);
  },
  CHANGE_SIDEBAR_TYPE(state, sidebarSize) {
    state.sidebarSize = sidebarSize;
    localStorage.setItem('layout.sidebarSize', sidebarSize);
  },
  CHANGE_TOPBAR(state, topbar) {
    state.topbar = topbar;
    localStorage.setItem('layout.topbar', topbar);
  },
  CHANGE_MODE(state, mode) {
    state.mode = mode;
    localStorage.setItem('layout.mode', mode);
  },
  CHANGE_POSITION(state, position) {
    state.position = position;
    localStorage.setItem('layout.position', position);
  },
  CHANGE_SIDEBAR_VIEW(state, sidebarView) {
    state.sidebarView = sidebarView;
    localStorage.setItem('layout.sidebarView', sidebarView);
  },
  CHANGE_SIDEBAR_COLOR(state, sidebarColor) {
    state.sidebarColor = sidebarColor;
    localStorage.setItem('layout.sidebarColor', sidebarColor);
  },
  CHANGE_SIDEBAR_IMAGE(state, sidebarImage) {
    state.sidebarImage = sidebarImage;
    localStorage.setItem('layout.sidebarImage', sidebarImage);
  },
  CHANGE_PRELOADER(state, preloader) {
    state.preloader = preloader;
    localStorage.setItem('layout.preloader', preloader);
  },
  CHANGE_VISIBILITY(state, visibility) {
    state.visibility = visibility;
    localStorage.setItem('layout.visibility', visibility);
  }
};

export const actions = {
  changeLayoutType({ commit }, { layoutType }) {
    commit('CHANGE_LAYOUT', layoutType);
    document.body.removeAttribute("style");
  },

  changeLayoutWidth({ commit }, { layoutWidth }) {
    commit('CHANGE_LAYOUT_WIDTH', layoutWidth);
  },

  changeSidebarSize({ commit }, { sidebarSize }) {
    commit('CHANGE_SIDEBAR_TYPE', sidebarSize);
  },

  changeTopbar({ commit }, { topbar }) {
    commit('CHANGE_TOPBAR', topbar);
  },

  changeMode({ commit }, { mode }) {
    commit('CHANGE_MODE', mode);
  },

  changePosition({ commit }, { position }) {
    commit('CHANGE_POSITION', position);
  },

  changeSidebarView({ commit }, { sidebarView }) {
    commit('CHANGE_SIDEBAR_VIEW', sidebarView);
  },

  changeSidebarColor({ commit }, { sidebarColor }) {
    commit('CHANGE_SIDEBAR_COLOR', sidebarColor);
  },

  changeSidebarImage({ commit }, { sidebarImage }) {
    commit('CHANGE_SIDEBAR_IMAGE', sidebarImage);
  },

  changePreloader({ commit }, { preloader }) {
    commit('CHANGE_PRELOADER', preloader);
  },

  changeVisibility({ commit }, { visibility }) {
    commit('CHANGE_VISIBILITY', visibility);
  }
};
